// The screen a visitor meets, drawn rather than argued.
//
// This was a product landing page: an eyebrow, a headline, a lede, three
// bullets making the case for the system, a sign-in card in the right column
// and a band of three "facts" beneath it. Nobody arrives at SIMSM to be
// persuaded — every reader of this page is an operator, an administrator or an
// observer who already knows what it is and wants in. The wireframe draws that
// and nothing else, and says why in its own header comment: "fără card:
// conținutul stă direct pe pagină, centrat optic la ~45%; butonul e singurul
// element saturat, deci e propria ancoră"
// (`wireframes/react/src/pages/Autentificare.tsx:1-2`, read-only, R6).
//
// So there is no card here either, despite the word in the finding: a column on
// the page's own ground, the AMDM logo above the system's name, one filled
// button, the support address. The gov.md strip above and the footer below are
// `PublicShell`'s (spec 032).
//
// The development login is the one thing on this screen the drawing does not
// have, because the drawing assumes MPass. Owner ruling of 2026-09-06: it stays,
// with its predefined roles and users. It sits directly beneath the MPass button
// and matches its shape — beneath because it is the stand-in, not an alternative
// of equal standing, and outlined because the drawing's rule is that the MPass
// button is the only saturated element. It carries no build flag: until MPass is
// wired this is the only working way into a deployed SIMSM, and a door that a
// missing environment variable can remove is not the open access the owner ruled
// for.
//
// Both buttons are `rectangular`, against the app's own habit. MUD's base button
// is a pill (`mud.css:207`, `.btn { border-radius: var(--r-full) }`) and the 192
// `shape="circular"` uses elsewhere are right to be pills — but the MPass button
// is the drawn exception: `.btn.badge-mpass` overrides to `var(--r8)`, 8px
// (`mud.css:348`), which is exactly AGE's `rectangular-lg`. The dev login follows
// its neighbour because the two share one slot and one job. This is a local
// exception to the pill, not a licence to square the rest of the app.
// per docs/ui-shape-entry-screens.md §The entry screen
import { useTranslate } from '@tolgee/react';
import { useNavigate } from 'react-router-dom';
import { MudButton, MudLink, MudLogo } from '@egov-moldova/mud-react';
import { DEV_LOGIN_ENABLED } from '../app/dev-login-flag';

export function HomePage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();

  return (
    <main className="simsm-entry">
      <div className="simsm-entry__column">
        <img
          src="/brand/amdm-logo.png"
          alt={t('home.amdm.logo_alt')}
          className="simsm-entry__logo"
          width={283}
          height={72}
        />

        {/* The drawing writes the mark and the full name as `<b>` over
            `<small>` (`mud.css:1211-1221`). The mark is this page's heading all
            the same: it is the one thing on the screen that names it, and every
            route owes a reader one. `h1` costs nothing visually — the drawn
            16px bold is what the rule below sets. */}
        <div className="simsm-entry__identity">
          <h1 className="simsm-entry__name">{t('home.product.mark')}</h1>
          <p className="simsm-entry__full-name">{t('footer.product.name')}</p>
        </div>

        <MudButton
          shape="rectangular"
          fullWidth
          variant="primary"
          appearance="filled"
          size="lg"
          className="simsm-entry__action"
          onClick={() => {
            window.location.href = '/api/v1/auth/login';
          }}
        >
          {/* The drawn button carries the MPass mark before its label
              (`mud.css:356`, `.badge-mpass .mark`). AGE ships it as a logo
              rather than an icon, and the stylesheet holds it to the drawn
              24px — mud-logo's own CSS invites a consumer to constrain the
              host and lets the SVG follow. */}
          <MudLogo slot="icon-start" name="mpass-logo-logomark-only" />
          {t('home.cta.login')}
        </MudButton>

        {DEV_LOGIN_ENABLED && (
          <MudButton
            shape="rectangular"
            fullWidth
            variant="strict"
            appearance="outlined"
            size="lg"
            className="simsm-entry__action simsm-entry__action--dev"
            onClick={() => navigate('/dev-login')}
          >
            {t('home.cta.dev_login')}
          </MudButton>
        )}

        {/* The drawing puts the support address on the login surface itself, not
            only in the subsol (`Autentificare.tsx:57-61`): a reader who cannot
            get past this screen must not have to scroll for it (caiet ACCS-10,
            UC10-06). */}
        <p className="simsm-entry__support">
          {t('footer.support.label')}{' '}
          <MudLink href={`mailto:${t('footer.contact.email')}`}>
            {t('footer.contact.email')}
          </MudLink>
        </p>
      </div>
    </main>
  );
}
